import { Button, Grid } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { useAppContext } from "../../../libs/app-core/AppContext";
import { DialogButtons } from "../../../libs/app-core/dialogs/DialogButtons";
import { InputDialog } from "../../../libs/app-core/dialogs/InputDialog";
import { MessageDialog } from "../../../libs/app-core/dialogs/MessageDialog";
import { Toast } from "../../../libs/app-core/Toast";
import { useGridApiRef } from "../../../libs/data-grid-view/useGridApiRef";
import { linear_v, linear_item, linear_h, } from "../../../libs/linear-layout/LinearLayout";
import Title from "../../../libs/rhf-mui/ui/Title";
import AppMenuButton from "../../components/AppMenuButton";
import { SpwkUtil } from "../../dao/SpwkUtil";
import { UniOption } from "../../dao/UniOption";
import { UniOptionDAO } from "../../dao/UniOptionDAO";
import { UniQuestion } from "../../dao/UniQuestion";
import { OnApiError } from "../../ErrorManager";
import { ROUTE_input_def } from "../../Routes";

const uni_form_columns: GridColDef[] = [
  { field: "serno", headerName: "serno", width: 85, editable: true },
  { field: "sort_no", headerName: "sort_no", width: 85, editable: true },

  { field: "v3_group", headerName: "v3_group", width: 180, editable: true },
  { field: "v3_code", headerName: "v3_code", width: 180, editable: true },

  {
    field: "variant",
    headerName: "variant",
    width: 90,
    editable: true,
    type: "singleSelect",
    valueOptions: [
      { value: "", label: "(空白)" },
      { value: "h6", label: "h6 主要章節標題" },
      { value: "subtitle2", label: "subtitle2 一般標題" },
      { value: "body2", label: "body2 題目標題" },
    ],
  },

  {
    field: "split",
    headerName: "split",
    width: 100,
    valueGetter: (params) => {
      let data = params.row as UniOption;
      let re = "";

      if (data.is_split_parent) {
        re += "(已拆分)";
      }

      if (data.split_from_option_guid) {
        re += " . . └───";
      }

      return re;
    },
  },

  { field: "o_his_code", headerName: "o_his_code", width: 180, editable: true },

  { field: "o_gov_code", headerName: "o_gov_code", width: 180, editable: true },

  { field: "option_code", headerName: "code", width: 200, editable: true },
  { field: "option_title", headerName: "title", width: 360, editable: true },
  { field: "triggers", headerName: "triggers", width: 360, editable: true },
  { field: "gender_filter", headerName: "gender_filter", width: 360, editable: true },

  { field: "title_eng", headerName: "英文", width: 500, editable: true },
  { field: "title_ind", headerName: "印尼", width: 500, editable: true },
  { field: "title_tha", headerName: "泰國", width: 500, editable: true },
  { field: "title_tgl", headerName: "菲律賓", width: 500, editable: true },
  { field: "title_vie", headerName: "越南", width: 500, editable: true },

  { field: "visibility", headerName: "visibility", width: 160, editable: true },

];

type Props = {
  CurrUniQuestion: UniQuestion | null;
  _UniOptionList: UniOption[];
  set_UniOptionList: (data_list: UniOption[]) => void;
  refresh_UniOptionList: () => Promise<void>;
};

const DgvUniOption = ({
  CurrUniQuestion,
  _UniOptionList,
  set_UniOptionList,
  refresh_UniOptionList,
}: Props) => {
  const ctx = useAppContext();

  const grid_api = useGridApiRef(uni_form_columns);

  /** 當前選擇的 Option */
  const [CurrOption, set_CurrOption] = useState<UniOption | null>(null);

  useEffect(() => {
    /* 題目改變時，取消當前 Option 的選擇*/
    set_CurrOption(null);
  }, [CurrUniQuestion]);

  return (
    <Grid {...linear_v}>
      <Grid {...linear_item}>{render_dgv選項_ActionBar()}</Grid>
      <Grid {...linear_item} flex={1}>
        {render_dgv選項_DataGrid()}
      </Grid>
    </Grid>
  );

  function btn_編輯Inputs_Click() {
    const data = getCurrRowData();
    if (data == null) {
      return;
    }

    const { input_list } = SpwkUtil.CreateInputTokens(data, null);
    if (input_list?.length === 0) {
      Toast.ShowWarning(ctx, "沒有需要設定的 Inputs");
      return;
    }

    ctx.Navigate(ROUTE_input_def.url + "?option=" + data.guid);
  }

  function btn_刪除_Click() {
    const data = getCurrRowData();
    if (data == null) {
      return;
    }

    MessageDialog.Show(
      ctx,
      data.serno + ":" + data.option_title,
      "是否刪除選定資料?",
      {
        ...DialogButtons.DeleteCancel,
        async onDialogResult(e) {
          if (e.DialogResult === "Yes") {
            try {
              await UniOptionDAO.Delete(data);

              set_UniOptionList([
                ..._UniOptionList.filter((x) => x.guid !== data.guid),
              ]);

              Toast.Show(ctx, "資料已刪除");
            } catch (ex: any) {
              OnApiError(ctx, ex);
            }
          } else {
            Toast.Show(ctx, "取消");
          }
        },
      }
    );
  }

  function render_dgv選項_ActionBar() {
    return (
      <Grid {...linear_h} alignItems="center" p={1}>
        <AppMenuButton
          label={<Title variant="subtitle2"> 選項列表: { }</Title>}
        />
        <Grid {...linear_item} flex={1}>
        </Grid>

        <Grid {...linear_item}>
          <Button onClick={btn_上移_Click} size="small">
            上移
          </Button>
        </Grid>

        <Grid {...linear_item}>
          <Button onClick={btn_下移_Click} size="small">
            下移
          </Button>
        </Grid>

        <Grid {...linear_item}>
          <Button onClick={btn_確定排序_Click} size="small">
            確定排序
          </Button>
        </Grid>

        <Grid {...linear_item}>|</Grid>

        <Grid {...linear_item}>
          <Button onClick={btn_新增選項_Click} size="small">
            新增選項
          </Button>
        </Grid>

        <Grid {...linear_item}>
          <Button variant="contained" onClick={btn_批次新增_Click} size="small">
            批次新增
          </Button>
        </Grid>

        <Grid {...linear_item}>
          <Button
            variant="contained"
            color="warning"
            onClick={btn_拆分選項_Click}
            size="small"
          >
            拆分選項
          </Button>
        </Grid>

        <Grid {...linear_item}>
          <Button onClick={btn_編輯Inputs_Click} size="small">
            編輯Inputs
          </Button>
        </Grid>

        <Grid {...linear_item}>
          <Button color="error" onClick={btn_刪除_Click} size="small">
            {"刪除"}
          </Button>
        </Grid>

        <Grid {...linear_item}>
          <Title>...</Title>
        </Grid>
      </Grid>
    );
  }

  function render_dgv選項_DataGrid() {
    return (
      <DataGrid
        style={{ width: "100%", backgroundColor: "white", padding: "12px" }}
        getRowId={(data) => {
          return data.guid;
        }}
        rows={_UniOptionList}
        columns={grid_api.columns}
        density={"compact"}
        pageSize={100}
        rowsPerPageOptions={[100]}
        hideFooterSelectedRowCount
        hideFooter
        onCellEditCommit={async ({ id, field, value }) => {
          try {
            const data = new UniOption();
            data.guid = id as string;

            await UniOptionDAO.UpdateOneField(
              data,
              field,
              value
            );

            refresh_UniOptionList();

            Toast.ShowScuccess(ctx, "儲存成功");
          } catch {
            Toast.ShowDanger(ctx, "儲存失敗!!!");
          }
        }}
        onRowClick={(params) => {
          const sel_data = params.row as UniOption;
          set_CurrOption(sel_data);
        }}
      />
    );
  }

  async function btn_新增選項_Click() {
    try {
      if (CurrUniQuestion == null || CurrUniQuestion.guid == null) {
        Toast.ShowWarning(ctx, "請先選擇問題");
        return;
      }

      await UniOptionDAO.Insert_新增空白選項(CurrUniQuestion.guid);

      await refresh_UniOptionList();

      Toast.Show(ctx, "新增選項");
    } catch (ex: any) {
      OnApiError(ctx, ex);
    }
  }

  async function btn_批次新增_Click() {
    try {
      if (CurrUniQuestion == null || CurrUniQuestion.guid == null) {
        Toast.ShowWarning(ctx, "請先選擇問題");
        return;
      }

      InputDialog.Show(
        ctx,
        '範例: "□咳嗽  □咳痰  □呼吸困難" ',
        "批次新增選項",
        "",
        {
          async onDialogResult(e) {
            if (e.DialogResult === "Yes") {
              const input = e.Data as string;

              let splits = input.split("□");

              let options: string[] = [];

              for (const s of splits) {
                if (s.trim() !== "") {
                  options.push(s.trim());
                }
              }
              for (const opt of options) {
                let new_opt = await UniOptionDAO.Insert(
                  CurrUniQuestion.guid,
                  opt,
                  opt
                );
                console.log("options of : ", new_opt.serno);
              }

              Toast.ShowInfo(ctx, "批次新增");

              await refresh_UniOptionList();
            } else {
              Toast.Show(ctx, "取消");
            }
          },
        }
      );
    } catch (ex: any) {
      OnApiError(ctx, ex);
    }
  }

  async function btn_拆分選項_Click() {
    if (CurrUniQuestion == null || CurrUniQuestion.guid == null) {
      Toast.ShowWarning(ctx, "請先選擇 問題");
      return;
    }

    if (CurrOption == null || CurrOption.guid == null) {
      Toast.ShowWarning(ctx, "請先選擇 選項");
      return;
    }

    MessageDialog.Show(
      ctx,
      "拆分選項:" + CurrOption.getV3Code(),
      "新增一個拆分選項?",
      {
        ...DialogButtons.OKCancel,
        yesText: "新增一個拆分選項",
        async onDialogResult(e) {
          if (e.DialogResult === "Yes") {
            await UniOptionDAO.Insert_拆分的子選項(CurrOption);

            await refresh_UniOptionList();
          } else {
            Toast.Show(ctx, "取消");
          }
        },
      }
    );
  }

  //#region 排序相關

  async function btn_確定排序_Click() {
    let ii = 1;
    for (let data of _UniOptionList) {
      await UniOptionDAO.Update_SetSortNo(data, ii);
      ii++;
    }

    set_UniOptionList([..._UniOptionList]);
    Toast.ShowScuccess(ctx, "已保存順序");
    // UniQuestion
  }

  function btn_上移_Click() {
    const data = getCurrRowData();
    if (data == null) {
      return;
    }

    const index = _UniOptionList.indexOf(data);

    if (index - 1 < 0) {
      Toast.ShowWarning(ctx, "無法上移");
      return;
    }

    set_UniOptionList(array_move([..._UniOptionList], index, index - 1));

    Toast.Show(ctx, "上移");
  }

  /** 取得當前 Row 的 Data */
  function getCurrRowData(): UniOption | null {
    let sel_rows = grid_api.apiRef.current?.getSelectedRows();

    if (sel_rows == null) {
      Toast.ShowWarning(ctx, "請選擇資料");
      return null;
    }

    const array = Array.from(sel_rows, ([key, value]) => value);

    if (array.length !== 1) {
      Toast.ShowWarning(ctx, "請選擇資料");
      return null;
    }

    const data = array[0] as UniOption;

    return data;
  }

  // https://stackoverflow.com/questions/5306680/move-an-array-element-from-one-array-position-to-another
  function array_move(arr: any[], old_index: number, new_index: number) {
    if (new_index >= arr.length) {
      var k = new_index - arr.length + 1;
      while (k--) {
        arr.push(undefined);
      }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    return arr; // for testing
  }

  function btn_下移_Click() {
    const data = getCurrRowData();
    if (data == null) {
      return;
    }

    const index = _UniOptionList.indexOf(data);

    if (index + 1 >= _UniOptionList.length) {
      Toast.ShowWarning(ctx, "無法下移");
      return;
    }

    set_UniOptionList(array_move([..._UniOptionList], index, index + 1));

    Toast.Show(ctx, "下移");
  }
  //#endregion
};

export default DgvUniOption;
